import { useRef, useEffect, useState } from 'react';

/**
 * Hook to enable drag-to-scroll functionality on an element
 * Works with both mouse and touch inputs (like Google Maps)
 * @param {React.RefObject} scrollRef - Reference to the scrollable element
 * @param {boolean} enabled - Whether drag scroll should be enabled (default: true)
 * @returns {Object} - Object with isDragging state
 */
export function useDragScroll(scrollRef, enabled = true) {
    const isDragging = useRef(false);
    const startX = useRef(0);
    const startY = useRef(0);
    const scrollStartX = useRef(0);
    const scrollStartY = useRef(0);
    const hasMoved = useRef(false);
    const moveThreshold = 5; // pixels
    const [isInitialized, setIsInitialized] = useState(false);

    // Check for element availability
    useEffect(() => {
        // Only initialize if enabled
        if (!enabled) {
            setIsInitialized(false);
            return;
        }
        
        const checkElement = () => {
            if (scrollRef.current && !isInitialized) {
                console.log('useDragScroll: Element found, initializing');
                setIsInitialized(true);
            }
        };

        // Check immediately
        checkElement();

        // Also check periodically until found
        const interval = setInterval(checkElement, 100);

        return () => clearInterval(interval);
    }, [scrollRef, isInitialized, enabled]);

    useEffect(() => {
        const element = scrollRef.current;
        if (!element) return;
        
        // If disabled, clean up any styling and return
        if (!enabled || !isInitialized) {
            element.style.cursor = '';
            return;
        }

        console.log('useDragScroll: Setting up drag handlers', {
            enabled,
            scrollWidth: element.scrollWidth,
            clientWidth: element.clientWidth,
            scrollHeight: element.scrollHeight,
            clientHeight: element.clientHeight
        });

        // Mouse event handlers
        const handleMouseDown = (e) => {
            // Only activate on left mouse button
            if (e.button !== 0) return;
            
            // Don't interfere with buttons or interactive elements
            if (e.target.closest('button, a, input, textarea, select')) return;
            
            // Prevent default to stop text selection
            e.preventDefault();
            
            isDragging.current = true;
            hasMoved.current = false;
            startX.current = e.clientX;
            startY.current = e.clientY;
            scrollStartX.current = element.scrollLeft;
            scrollStartY.current = element.scrollTop;
            
            // Change cursor to grabbing
            element.style.cursor = 'grabbing';
            element.style.userSelect = 'none';
            
            // Add capturing to document to handle mouse move outside element
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        };

        const handleMouseMove = (e) => {
            if (!isDragging.current) return;
            
            e.preventDefault();
            
            const deltaX = e.clientX - startX.current;
            const deltaY = e.clientY - startY.current;
            
            // Check if we've moved enough to consider it a drag
            if (!hasMoved.current && (Math.abs(deltaX) > moveThreshold || Math.abs(deltaY) > moveThreshold)) {
                hasMoved.current = true;
            }
            
            element.scrollLeft = scrollStartX.current - deltaX;
            element.scrollTop = scrollStartY.current - deltaY;
        };

        const handleMouseUp = (e) => {
            if (!isDragging.current) return;
            
            // If we dragged, prevent the click
            if (hasMoved.current) {
                e.preventDefault();
                e.stopPropagation();
                // Set a flag that will be checked by click handler
                element.setAttribute('data-drag-ended', 'true');
                setTimeout(() => {
                    element.removeAttribute('data-drag-ended');
                    hasMoved.current = false;
                }, 50);
            }
            
            isDragging.current = false;
            element.style.cursor = 'grab';
            element.style.userSelect = '';
            
            // Remove document listeners
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        // Touch event handlers
        const handleTouchStart = (e) => {
            // Don't interfere with buttons or interactive elements
            if (e.target.closest('button, a, input, textarea, select')) return;
            
            const touch = e.touches[0];
            isDragging.current = true;
            startX.current = touch.clientX;
            startY.current = touch.clientY;
            scrollStartX.current = element.scrollLeft;
            scrollStartY.current = element.scrollTop;
        };

        const handleTouchMove = (e) => {
            if (!isDragging.current) return;
            
            e.preventDefault();
            
            const touch = e.touches[0];
            const deltaX = touch.clientX - startX.current;
            const deltaY = touch.clientY - startY.current;
            
            element.scrollLeft = scrollStartX.current - deltaX;
            element.scrollTop = scrollStartY.current - deltaY;
        };

        const handleTouchEnd = () => {
            isDragging.current = false;
        };

        // Prevent clicks after dragging
        const handleClick = (e) => {
            if (element.getAttribute('data-drag-ended') === 'true') {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        // Add cursor style
        element.style.cursor = 'grab';

        // Add event listeners
        element.addEventListener('mousedown', handleMouseDown);
        element.addEventListener('click', handleClick, true); // Capture phase
        
        // Touch events
        element.addEventListener('touchstart', handleTouchStart, { passive: false });
        element.addEventListener('touchmove', handleTouchMove, { passive: false });
        element.addEventListener('touchend', handleTouchEnd);
        element.addEventListener('touchcancel', handleTouchEnd);

        // Cleanup
        return () => {
            element.removeEventListener('mousedown', handleMouseDown);
            element.removeEventListener('click', handleClick, true);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            
            element.removeEventListener('touchstart', handleTouchStart);
            element.removeEventListener('touchmove', handleTouchMove);
            element.removeEventListener('touchend', handleTouchEnd);
            element.removeEventListener('touchcancel', handleTouchEnd);
            
            element.style.cursor = '';
        };
    }, [isInitialized, enabled, scrollRef]); // Re-run when initialized, enabled or scrollRef changes

    return { isDragging: isDragging.current };
}

export default useDragScroll;